import type {QueryParams} from '@wix/document-manager-utils'
import type {AppDefinitionId, ApplicationId, Callback1, PS} from '@wix/document-services-types'
import _ from 'lodash'
import {tpa, warmupUtils} from '@wix/santa-ds-libs'
import siteMetadata from '../../siteMetadata/siteMetadata'
import clientSpecMapService from './clientSpecMapService'

const {ajaxLibrary} = warmupUtils
const premiumAppsUrl = _.template('<%= premiumStateAPIUrl %>?siteGuid=<%= metasiteId %>')
const YEARLY = 6

const getPremiumStateTopology = function (ps: PS) {
    const serviceTopology = ps.pointers.general.getServiceTopology()
    const premiumStatePointer = ps.pointers.getInnerPointer(serviceTopology, 'premiumStateApiUrl')
    return ps.dal.get(premiumStatePointer) // 'https://editor.wix.com/_api/wix/getTpaPremiumState'
}

const getPremiumApps = function (ps: PS, metasiteId: string, onSuccess: Callback1<any>, onError?: Callback1<any>) {
    const base = getPremiumStateTopology(ps)
    const url = premiumAppsUrl({
        premiumStateAPIUrl: base,
        metasiteId
    })
    ajaxLibrary.ajax({
        type: 'POST',
        url,
        data: {},
        dataType: 'json',
        success(data) {
            if (_.isFunction(onSuccess)) {
                onSuccess(data.payload)
            }
        },
        error: onError
    })
}

const isYearly = function (packageData) {
    return packageData.cycle === YEARLY
}

const extractYearlyPremiumApps = function (json) {
    const yearlyAppDefIds = []
    const packageData = _.get(json, 'tpaPackages') || []
    if (_.isArray(packageData)) {
        _.forEach(packageData, function (packag) {
            if (isYearly(packag)) {
                yearlyAppDefIds.push(packag.appDefinitionId)
            }
        })
    } else if (_.isObject(packageData)) {
        if (isYearly(packageData)) {
            // @ts-expect-error
            yearlyAppDefIds.push(packageData.appDefinitionId)
        }
    }
    return yearlyAppDefIds
}

const getSiteUpgradeUrl = function (ps: PS, queryParameters?: QueryParams) {
    const serviceTopology = ps.dal.get(ps.pointers.general.getServiceTopology())
    const baseUpgradeUrl = `${serviceTopology.premiumServerUrl}/wix/api/premiumStart`
    const metaSiteId = siteMetadata.generalInfo.getMetaSiteId(ps)
    queryParameters = queryParameters || {}
    queryParameters.siteGuid = metaSiteId

    return new tpa.common.TpaBaseUrlBuilder(baseUpgradeUrl).addMultipleQueryParams(queryParameters).build()
}

const getAppUpgradeUrl = function (
    ps: PS,
    appDefinitionId: AppDefinitionId,
    vendorProductId: string,
    paymentCycle?: string,
    options?
) {
    const appData = clientSpecMapService.getAppDataByAppDefinitionId(ps, appDefinitionId)
    if (_.isNil(appDefinitionId) || _.isNil(appData)) {
        return
    }
    const serviceTopology = ps.dal.get(ps.pointers.general.getServiceTopology())
    const baseUpgradeUrl = `${serviceTopology.premiumServerUrl}/wix/api/tpaStartPurchase`
    const metaSiteId = siteMetadata.generalInfo.getMetaSiteId(ps)
    options = options || {}
    const params: any = {
        applicationId: appData.applicationId,
        vendorProductId,
        paymentCycle: paymentCycle || 'MONTHLY',
        appDefinitionId: appData.appDefinitionId,
        metaSiteId,
        pp_type: options.pp_type,
        referralAdditionalInfo: options.referralAdditionalInfo
    }
    if (options.premiumIntent) {
        params.premiumIntent = options.premiumIntent
    }

    return new tpa.common.TpaBaseUrlBuilder(baseUpgradeUrl).addMultipleQueryParams(params).build()
}

const getAppUpgradeUrlByAppId = function (
    ps: PS,
    applicationId: ApplicationId,
    vendorProductId: string,
    paymentCycle?: string,
    options?
) {
    if (applicationId) {
        const appDefId = clientSpecMapService.getAppDefinitionIdFromApplicationId(ps, applicationId, {
            source: 'getAppUpgradeUrlByAppId',
            options
        })
        if (!appDefId) {
            return
        }
        return getAppUpgradeUrl(ps, appDefId, vendorProductId, paymentCycle, options)
    }
}

export default {
    getPremiumApps,
    extractYearlyPremiumApps,
    isYearly,
    getSiteUpgradeUrl,
    getAppUpgradeUrl,
    getAppUpgradeUrlByAppId
}
